import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { isEmpty } from 'lodash';

import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { environment } from '../../../../environments/environment';
import { AppState } from '../../../store/state/app.state';
import { ResponseModel } from '../../../shared/models';
import { VatDictionaryItemModel } from '../models';
import { selectCompanyProfile } from '../../../administration/store/selectors';
import { CompanyProfile } from '../../../administration/models/company-profile.model';
import { DisplayToaster } from '../../../shared/decorators/toaster';
import { VatRatesFlowEnum } from '../../../shared/enums';


@Injectable({
  providedIn: 'root'
})
export class VatDictionaryService {

  private wid: number;

  private apiUrl(url: string = ''): string {
    return environment.javaApiVersion + `/workspaces/${this.wid}/vat-dictionary` + url;
  }

  constructor(
    private readonly toasterService: ToasterService,
    private readonly http: HttpClient,
    private readonly store: Store<AppState>,
  ) {
    this.store.select(selectCompanyProfile)
      .pipe(filter(cp => !!cp && !isEmpty(cp)))
      .subscribe((profile: CompanyProfile) => {
        this.wid = profile.workspaceId;
      });
  }

  @DisplayToaster({showErrorMessage: true})
  public getVatDictionary(countryIso3: string[]): Observable<VatDictionaryItemModel[]> {
    const params = {
      countries: countryIso3.toString(),
    };
    return this.http.get<ResponseModel<VatDictionaryItemModel[]>>(this.apiUrl(), {params})
      .pipe(map((response: ResponseModel<VatDictionaryItemModel[]>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public addVatDictionaryItm(countryIso3: string, rate): Observable<VatDictionaryItemModel> {
    const body = {
      countryCode: countryIso3,
      rate
    };
    return this.http.post<ResponseModel<VatDictionaryItemModel>>(this.apiUrl(), body)
      .pipe(map((response: ResponseModel<VatDictionaryItemModel>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public updateRateVatDictionaryItm(uuid: string, rate): Observable<null> {
    const body = {
      // countryCode: countryIso3,
      rate
    };
    return this.http.patch<null>(this.apiUrl(`/${uuid}/rate`), body);
    // .pipe(map((response: ResponseModel<VatDictionaryItemModel>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public showVatDictionaryItm(uuid: string): Observable<VatDictionaryItemModel> {
    return this.http.request<ResponseModel<VatDictionaryItemModel>>('patch', this.apiUrl(`/${uuid}/show`))
      .pipe(map((response: ResponseModel<VatDictionaryItemModel>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public hideVatDictionaryItm(uuid: string): Observable<VatDictionaryItemModel> {
    return this.http.request<ResponseModel<VatDictionaryItemModel>>('patch', this.apiUrl(`/${uuid}/hide`))
      .pipe(map((response: ResponseModel<VatDictionaryItemModel>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public setPrimaryVatDictionaryItm(uuid: string): Observable<VatDictionaryItemModel> {
    return this.http.request<ResponseModel<VatDictionaryItemModel>>('patch', this.apiUrl(`/${uuid}/primary`))
      .pipe(map((response: ResponseModel<VatDictionaryItemModel>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public setOrderPrimaryVatDictionaryItm(uuid: string, order: number): Observable<VatDictionaryItemModel> {
    return this.http.request<ResponseModel<VatDictionaryItemModel>>('patch', this.apiUrl(`/${uuid}/order/${order}`))
      .pipe(map((response: ResponseModel<VatDictionaryItemModel>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public deleteVatDictionaryItm(uuid: string): Observable<null> {
    return this.http.delete<ResponseModel<null>>(this.apiUrl(`/${uuid}`))
      .pipe(map((response: ResponseModel<null>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public getVatDictionaryRates(
    countryIso3: string[],
    flow: VatRatesFlowEnum = VatRatesFlowEnum.OUTGOING
  ): Observable<number[]> {
    const params = {
      countries: countryIso3.toString(),
      flow,
    };
    return this.http.get<ResponseModel<number[]>>(this.apiUrl('/rates'), {params})
      .pipe(map((response: ResponseModel<number[]>) => {
        const rates = response.data || [];
        return [...rates.filter(rate => +rate !== 0), 0];
      }));
  }


}
